// ── Entry-animation coordinator (shared by WidgetCards & WidgetCardsLandscape) ──
// Module-level state shared by ALL ticket widgets on the page, both portrait and
// landscape, so parallel widgets coordinate their first-load work.

// ── Per-widget animation stagger ──────────────────────────────────────────────
// When multiple widgets mount simultaneously they all resolve document.fonts.ready
// at the same instant and queue their initial-load calculations in the same
// 2-frame window, overwhelming the JS thread and causing the entry animation to
// stutter or skip.  This counter staggers each widget's first-load delay by
// _STAGGER_MS so their DOM measurements, state updates, and RAF chains are spread
// across distinct frames.
let _batchStartTime = 0;
let _batchCount = 0;
const _BATCH_WINDOW_MS = 400; // resets if no new widget starts within this window
const _STAGGER_MS = 80;       // ms between successive widget animation starts

export function _getWidgetAnimStagger() {
    const now = Date.now();
    if (now - _batchStartTime > _BATCH_WINDOW_MS) {
        _batchStartTime = now;
        _batchCount = 0;
    }
    return (_batchCount++) * _STAGGER_MS;
}

// ── Global entry-animation tracker ────────────────────────────────────────────
// Counts widgets (across the whole page) currently playing their entry animation.
// Heavy non-urgent work — like the expand-replay calc of an overflowing widget,
// which renders hundreds of hidden cards — waits via _whenNoEntryAnimations until
// every animation has finished, so it can never steal frames from a running
// animation.  A max-wait timeout guards against leaked counts.
let _animatingWidgets = 0;
const _animIdleWaiters = [];

export function _entryAnimStarted(countedRef) {
    if (countedRef.current) return;
    countedRef.current = true;
    _animatingWidgets++;
}

export function _entryAnimEnded(countedRef) {
    if (!countedRef.current) return;
    countedRef.current = false;
    _animatingWidgets = Math.max(0, _animatingWidgets - 1);
    if (_animatingWidgets === 0) _drainNext();
}

// Release waiters ONE at a time, not all at once.  Releasing them together made
// a deferred widget's new entry animation and another widget's heavy silent
// commit start in the same frames and collide (the exact stutter this module
// exists to prevent).  After running one waiter:
//   - if it started an animation, the counter went up and the next waiter is
//     released by _entryAnimEnded when that animation finishes;
//   - if it was silent work, the follow-up timer drains the next waiter after
//     a gap that lets the commit/layout settle.
const _DRAIN_GAP_MS = 350;
let _drainTimer = null;
function _drainNext() {
    if (_animatingWidgets > 0 || _animIdleWaiters.length === 0) return;
    const w = _animIdleWaiters.shift();
    w();
    if (_drainTimer) clearTimeout(_drainTimer);
    _drainTimer = setTimeout(() => { _drainTimer = null; _drainNext(); }, _DRAIN_GAP_MS);
}

export function _whenNoEntryAnimations(fn, maxWaitMs = 8000) {
    let done = false;
    let t = null;
    const run = () => {
        if (done) return;
        done = true;
        if (t) clearTimeout(t);
        fn();
    };
    if (_animatingWidgets === 0 && _animIdleWaiters.length === 0) { run(); return; }
    t = setTimeout(() => {
        const i = _animIdleWaiters.indexOf(run);
        if (i >= 0) _animIdleWaiters.splice(i, 1);
        run();
    }, maxWaitMs);
    _animIdleWaiters.push(run);
    // If nothing is animating right now, kick the drain so queued work proceeds.
    if (_animatingWidgets === 0) _drainNext();
}
